import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/pro-light-svg-icons/faTriangleExclamation';
import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark';
import { TypedMemo } from '../../../common/types';
import { editorMessages } from '../editorMessages';
import { EditorPublish } from './useEditorPublish';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for several parts; each part uses its own slice of it */
import styles from './SMLEditorTopBar.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SMLEditorPublishAlertProps {
  address: string;
  publish: EditorPublish;
  onOpenPageTab: () => void;
}

/**
 * The line under the top bar when a publish fails (design a4, 3): the address is taken (with a free
 * address to use), or another error. The draft stays as it is.
 */
const SMLEditorPublishAlertComponent: React.FC<SMLEditorPublishAlertProps> = ({ address, publish, onOpenPageTab }) => {
  const intl = useIntl();
  const { state, applySuggestion, dismiss } = publish;
  const suggestion = state.kind === 'slugTaken' ? state.suggestion : null;
  const onUse = useCallback(() => {
    if (suggestion !== null) {
      applySuggestion(suggestion);
    }
  }, [suggestion, applySuggestion]);
  const onPick = useCallback(() => {
    dismiss();
    onOpenPageTab();
  }, [dismiss, onOpenPageTab]);

  if (state.kind !== 'slugTaken' && state.kind !== 'failed' && state.kind !== 'saveFailed') {
    return null;
  }

  return (
    <div className={styles.alert} role="alert" data-testid="editor-publish-alert">
      <FontAwesomeIcon icon={faTriangleExclamation} className={styles.alertIcon} />
      <span className={styles.alertText}>
        {state.kind === 'slugTaken' && intl.formatMessage(editorMessages.slugTaken, { address })}
        {state.kind === 'failed' && intl.formatMessage(editorMessages.publishFailed)}
        {state.kind === 'saveFailed' && intl.formatMessage(editorMessages.saveFailed)}
      </span>
      {state.kind === 'slugTaken' && (
        <>
          {suggestion !== null && (
            <button type="button" className={styles.alertAction} onClick={onUse}>
              {intl.formatMessage(editorMessages.slugTakenUse, { slug: suggestion })}
            </button>
          )}
          <button type="button" className={styles.alertLink} onClick={onPick}>
            {intl.formatMessage(editorMessages.slugTakenPick)}
          </button>
        </>
      )}
      <button
        type="button"
        className={styles.alertClose}
        onClick={dismiss}
        aria-label={intl.formatMessage(editorMessages.close)}
      >
        <FontAwesomeIcon icon={faXmark} />
      </button>
    </div>
  );
};

export const SMLEditorPublishAlert = TypedMemo(SMLEditorPublishAlertComponent);
